import { applyMove } from './core/rules.js';
import { generateLevel } from './core/generator.js';
import type { Color, GameState } from './core/types.js';
import { cellAt } from './core/board.js';
import { createRenderer } from './renderer/renderer.js';
import { buildTopbar, type TopbarHandle } from './ui/topbar.js';
import { buildColorbar, type ColorbarHandle } from './ui/colorbar.js';
import { showHelp, showLose, showStart, showWin } from './ui/overlays.js';
import { clear } from './ui/dom.js';
import {
  playFail,
  playFlip,
  playPenalty,
  playPick,
  playWin,
  setMuted,
} from './audio/sfx.js';
import { unlockAudio } from './audio/audioCtx.js';
import { loadLevel, loadMuted, loadSeed, newSeed, saveLevel, saveMuted } from './storage.js';

interface Ctx {
  state: GameState;
  level: number;
  seed: number;
  busy: boolean;
}

const buildShell = (
  root: HTMLElement,
): { topbar: TopbarHandle; colorbar: ColorbarHandle; host: HTMLElement } => {
  clear(root);
  const topbar = buildTopbar();
  const host = document.createElement('div');
  host.className = 'canvas-host';
  const colorbar = buildColorbar();
  root.append(topbar.element, host, colorbar.element);
  return { topbar, colorbar, host };
};

export const startGame = (root: HTMLElement): void => {
  const { topbar, colorbar, host } = buildShell(root);
  const renderer = createRenderer(host);
  const ctx: Ctx = {
    state: generateLevel(loadLevel(), loadSeed()),
    level: loadLevel(),
    seed: loadSeed(),
    busy: false,
  };

  const sync = (): void => {
    topbar.setLevel(ctx.state.board.level);
    topbar.setMoves(ctx.state.movesUsed, ctx.state.moveBudget);
    const cur = cellAt(ctx.state.board, ctx.state.board.start).color;
    colorbar.render(ctx.state.board.palette, cur, ctx.busy || ctx.state.status !== 'playing');
  };

  const loadCurrent = (): void => {
    ctx.state = generateLevel(ctx.level, ctx.seed);
    renderer.setBoard(ctx.state.board);
    sync();
  };

  const advance = (delta: number): void => {
    ctx.level = Math.max(1, ctx.level + delta);
    ctx.seed = newSeed();
    saveLevel(ctx.level);
    loadCurrent();
  };

  const onPick = (c: Color): void => {
    if (ctx.busy || ctx.state.status !== 'playing') return;
    unlockAudio();
    const result = applyMove(ctx.state, c);
    if (result.rejected) return;
    playFlip();
    if (result.effects.some((e) => e.type === 'penalty')) playPenalty();
    ctx.busy = true;
    ctx.state = result.state;
    renderer.applyEffects(result.effects, ctx.state.board);
    sync();
    setTimeout(() => {
      ctx.busy = false;
      sync();
      if (ctx.state.status === 'won') {
        playWin();
        showWin(root, ctx.state.board.level, ctx.state.movesUsed, ctx.state.moveBudget, () =>
          advance(+1),
        );
      } else if (ctx.state.status === 'lost') {
        playFail();
        showLose(root, () => loadCurrent());
      }
    }, 700);
  };

  colorbar.onPick(onPick);
  topbar.onMute((m) => {
    setMuted(m);
    saveMuted(m);
  });
  topbar.onReset(() => loadCurrent());
  topbar.onHelp(() => showHelp(root));
  topbar.setMuted(loadMuted());
  setMuted(loadMuted());

  host.addEventListener('pointerdown', (ev) => {
    unlockAudio();
    if (ctx.busy || ctx.state.status !== 'playing') return;
    const c = renderer.pickAt(ev);
    if (!c) return;
    const cell = cellAt(ctx.state.board, c);
    if (cell.tile === 'locked') return;
    if (cell.color === cellAt(ctx.state.board, ctx.state.board.start).color) return;
    playPick();
    onPick(cell.color);
  });

  renderer.setBoard(ctx.state.board);
  sync();
  showStart(
    root,
    ctx.state.board.level,
    () => unlockAudio(),
    () => showHelp(root),
  );
};
